import React from 'react'
import './login-email-verification-panel.scss'

const OTP_LENGTH = 6

export type PendingEmailVerification = {
  email: string
  userId?: number
}

type LoginEmailVerificationPanelProps = {
  pending: PendingEmailVerification
  otpDigits: string[]
  onOtpChange: (index: number, value: string) => void
  onOtpKeyDown: (index: number, event: React.KeyboardEvent<HTMLInputElement>) => void
  onOtpPaste: (event: React.ClipboardEvent<HTMLDivElement>) => void
  onVerify: () => void
  onResend: () => void
  isResending: boolean
  isVerifying: boolean
  setOtpInputRef: (index: number, el: HTMLInputElement | null) => void
}

const LoginEmailVerificationPanel = ({
  pending,
  otpDigits,
  onOtpChange,
  onOtpKeyDown,
  onOtpPaste,
  onVerify,
  onResend,
  isResending,
  isVerifying,
  setOtpInputRef,
}: LoginEmailVerificationPanelProps) => {
  const canVerify =
    pending.userId !== null && otpDigits.join('').length === OTP_LENGTH && !isVerifying

  return (
    <div
      className='login-page__email-verify-panel'
      role='alert'
      data-testid='login-email-not-verified-panel'
    >
      <p className='login-page__email-verify-panel-text'>
        Please verify your email before logging in.
      </p>
      <button
        type='button'
        className='login-page__email-verify-resend'
        onClick={onResend}
        disabled={isResending}
        data-testid='login-resend-verification-button'
      >
        {isResending ? 'Sending…' : 'Resend verification email'}
      </button>

      {pending.userId !== null && (
        <>
          <p className='login-page__email-verify-otp-hint'>
            Enter the 6-digit code from your email (check spam). After verifying, log in again with
            your password.
          </p>
          <div
            className='login-page__email-verify-otp-row'
            onPaste={onOtpPaste}
            data-testid='login-email-otp-row'
          >
            {otpDigits.map((digit, index) => (
              <input
                key={`login-otp-${index}`}
                ref={(el) => setOtpInputRef(index, el)}
                className='login-page__email-verify-otp-digit'
                inputMode='numeric'
                type='text'
                maxLength={1}
                value={digit}
                onChange={(e) => onOtpChange(index, e.target.value)}
                onKeyDown={(e) => onOtpKeyDown(index, e)}
                aria-label={`Verification code digit ${index + 1}`}
              />
            ))}
          </div>
          <button
            type='button'
            className='login-page__email-verify-submit'
            disabled={!canVerify}
            onClick={onVerify}
            data-testid='login-verify-email-otp-button'
          >
            {isVerifying ? 'Verifying…' : 'Verify email'}
          </button>
        </>
      )}
    </div>
  )
}

export { LoginEmailVerificationPanel, OTP_LENGTH }
